import { useEffect, useRef } from "react";
import { F, mediaKind, type Row } from "./types";
import { avgColor, blurhashOf, formatDuration, mediaBox, thumbSrc } from "./rows";
import { paintBlurhash } from "./blur";

/** A photo or a video poster, in a box whose height was known before the fetch
 *  started. The order matters and it is the whole reason a fast flick stays
 *  smooth:
 *
 *    average colour  ->  blurhash  ->  thumb  ->  the viewer, on tap
 *
 *  The first is free (25 ns) and paints on the very first frame, so the box is
 *  never blank. The second is 0.16 ms. The third crosses the network. Nothing
 *  in that sequence changes the box's size.
 *
 *  P5 swapped the original in here and played video in place. Both moved to the
 *  viewer in P6, and the row is better for it: it now mounts no media element
 *  of its own, so a <video> can never be left decoding behind a row you have
 *  already scrolled past.
 *
 *  It is a <button> rather than a div because the viewer has to hand focus back
 *  to something when it closes -- otherwise focus falls to the top of the
 *  document and the next keypress scrolls three years to the beginning.
 */
export function Media({ row, vw, vh, onOpen }: {
  row: Row;
  vw: number;
  vh: number;
  onOpen?: (el: HTMLElement) => void;
}) {
  const box = mediaBox(row, vw, vh)!;
  const hash = blurhashOf(row);
  const isVideo = mediaKind(row) === "video";
  const canvas = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (hash && canvas.current) paintBlurhash(canvas.current, hash);
  }, [hash]);

  return (
    <button
      type="button"
      className="media tappable"
      aria-label={isVideo ? "open video" : "open photo"}
      style={{ width: box.width, height: box.height, backgroundColor: avgColor(hash) }}
      onClick={(e) => onOpen?.(e.currentTarget)}
    >
      {hash && <canvas ref={canvas} className="blur" width={32} height={32} />}
      {/* decoding="async" keeps the image decode off the scroll frame. */}
      <img src={thumbSrc(row)!} decoding="async" alt="" />
      {isVideo && <span className="dur">{formatDuration(row[F.DurationS])}</span>}
    </button>
  );
}
